/**
 * Utility functions for pie chart calculations
 */

/**
 * Calculate SVG path for a pie slice with improved precision
 * @param {number} centerX - Center X coordinate
 * @param {number} centerY - Center Y coordinate
 * @param {number} radius - Radius of the pie
 * @param {number} startAngle - Start angle in radians
 * @param {number} endAngle - End angle in radians
 * @returns {string} SVG path string
 */
export const createPieSlicePath = (centerX, centerY, radius, startAngle, endAngle) => {
  // Use higher precision for smoother edges
  const precision = 6;
  const x1 = parseFloat((centerX + radius * Math.cos(startAngle)).toFixed(precision));
  const y1 = parseFloat((centerY + radius * Math.sin(startAngle)).toFixed(precision));
  const x2 = parseFloat((centerX + radius * Math.cos(endAngle)).toFixed(precision));
  const y2 = parseFloat((centerY + radius * Math.sin(endAngle)).toFixed(precision));
  
  const largeArcFlag = endAngle - startAngle <= Math.PI ? "0" : "1";
  
  return [
    "M", centerX, centerY,
    "L", x1, y1,
    "A", radius, radius, 0, largeArcFlag, 1, x2, y2,
    "Z"
  ].join(" ");
};

/**
 * Parse a currency string to a number
 * @param {string|number} value - The currency value to parse
 * @returns {number} The parsed numeric value
 */
const parseCurrencyToNumber = (value) => {
  if (!value || value === 'N/A' || value === '') return 0;
  if (typeof value === 'number') return value;
  
  // Handle string values
  const stringValue = String(value).trim();
  if (stringValue === '' || stringValue === 'N/A') return 0;
  
  // Remove currency symbols, commas, and spaces
  const cleanValue = stringValue.replace(/[$,\s]/g, '');
  
  // Handle compact notation (K, M, B)
  if (cleanValue.includes('K')) {
    return parseFloat(cleanValue.replace('K', '')) * 1000;
  }
  if (cleanValue.includes('M')) {
    return parseFloat(cleanValue.replace('M', '')) * 1000000;
  }
  if (cleanValue.includes('B')) {
    return parseFloat(cleanValue.replace('B', '')) * 1000000000;
  }
  
  return parseFloat(cleanValue) || 0;
};

/**
 * Format a number as currency (USD)
 * @param {number|string} value - The value to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.compact - Whether to use compact notation (K, M, B)
 * @returns {string} Formatted currency string
 */
const formatCurrency = (value, options = {}) => {
  const { compact = false } = options;
  
  const numValue = parseCurrencyToNumber(value);
  if (numValue === 0) return '$0';
  
  // Compact notation for large numbers
  if (compact) {
    if (numValue >= 1000000000) {
      return '$' + (numValue / 1000000000).toFixed(1) + 'B';
    } else if (numValue >= 1000000) {
      return '$' + (numValue / 1000000).toFixed(1) + 'M';
    } else if (numValue >= 1000) {
      return '$' + (numValue / 1000).toFixed(0) + 'K';
    }
    return '$' + numValue.toFixed(0);
  }
  
  // Full notation with proper formatting
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numValue);
};

/**
 * Calculate tier distribution for a country with profit information
 * @param {Array} clientData - Array of client data
 * @param {string} countryName - Name of the country
 * @returns {Object} Tier distribution with counts, percentages, and profit
 */
export const calculateTierDistribution = (clientData, countryName) => {
  if (!clientData || !clientData.length) {
    return { tiers: [], totalClients: 0, totalProfit: 0 };
  }
  
  // Find clients from this country
  const countryClients = clientData.filter(client => 
    client.country === countryName
  );
  
  if (countryClients.length === 0) {
    return { tiers: [], totalClients: 0, totalProfit: 0 };
  }
  
  // Count tiers and calculate profit
  const tierData = {};
  let totalProfit = 0;
  
  countryClients.forEach(client => {
    const tier = client['Tier'] || 'Unknown';
    const profit = parseCurrencyToNumber(client['Total Revenue'] || 0);
    
    if (!tierData[tier]) {
      tierData[tier] = {
        count: 0,
        profit: 0
      };
    }
    
    tierData[tier].count += 1;
    tierData[tier].profit += profit;
    totalProfit += profit;
  });
  
  const totalClients = countryClients.length;
  
  // Convert to array with percentages and sort by count (descending)
  const tiers = Object.entries(tierData)
    .map(([tier, data]) => ({
      tier,
      count: data.count,
      percentage: (data.count / totalClients) * 100,
      profit: data.profit,
      formattedProfit: formatCurrency(data.profit, { compact: true })
    }))
    .sort((a, b) => b.count - a.count);
  
  return { 
    tiers, 
    totalClients, 
    totalProfit,
    formattedTotalProfit: formatCurrency(totalProfit, { compact: true })
  };
};

/**
 * Generate pie slice data for rendering
 * @param {Array} tierDistribution - Array of tier objects with percentages
 * @param {number} radius - Radius of the pie chart
 * @returns {Array} Array of slice data with paths and colors
 */
export const generatePieSlices = (tierDistribution, radius = 10) => {
  if (!tierDistribution || tierDistribution.length === 0) {
    return [];
  }
  
  const centerX = radius;
  const centerY = radius;
  let currentAngle = -Math.PI / 2; // Start at top (12 o'clock)
  
  return tierDistribution.map(({ tier, percentage }) => {
    const sliceAngle = (percentage / 100) * 2 * Math.PI;
    const endAngle = currentAngle + sliceAngle;
    
    const path = createPieSlicePath(
      centerX,
      centerY,
      radius,
      currentAngle,
      endAngle
    );
    
    const slice = {
      tier,
      percentage,
      path,
      startAngle: currentAngle,
      endAngle
    };
    
    currentAngle = endAngle;
    return slice;
  });
};

/**
 * Check if a country has multiple tiers
 * @param {Array} clientData - Array of client data
 * @param {string} countryName - Name of the country
 * @returns {boolean} True if country has more than one tier
 */
export const hasMultipleTiers = (clientData, countryName) => {
  const { tiers } = calculateTierDistribution(clientData, countryName);
  return tiers.length > 1;
};

/**
 * Get the dominant tier for a country (for fallback to regular marker)
 * @param {Array} clientData - Array of client data
 * @param {string} countryName - Name of the country
 * @returns {string} The dominant tier
 */
export const getDominantTier = (clientData, countryName) => {
  const { tiers } = calculateTierDistribution(clientData, countryName);
  return tiers.length > 0 ? tiers[0].tier : 'Unknown';
};

/**
 * Create SVG circle for single-tier countries to match pie chart style
 * @param {number} radius - Radius of the circle
 * @param {string} fillColor - Fill color for the circle
 * @param {string} strokeColor - Stroke color for the circle
 * @returns {string} SVG string for the circle
 */
export const createSingleTierCircleSvg = (radius, fillColor, strokeColor = '#333') => {
  const size = radius * 2;
  const centerX = radius;
  const centerY = radius;
  
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg" style="shape-rendering: geometricPrecision;">
    <circle cx="${centerX}" cy="${centerY}" r="${radius - 0.5}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="1" style="shape-rendering: geometricPrecision;"/>
  </svg>`;
};
